import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileText, RotateCcw, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Eyebrow } from "../components/common";
import Seo from "../components/Seo";
import { company } from "../mock";

function LegalShell({ icon: Icon, eyebrow, title, updated, intro, sections, footNote, seoTitle, seoDescription }) {
  return (
    <div className="overflow-hidden">
      <Seo title={seoTitle} description={seoDescription} />
      <section className="relative hero-gradient pt-32 sm:pt-40 pb-14">
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 animate-fade-up">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h1 className="mt-6 flex items-center gap-3 text-[28px] sm:text-[48px] font-extrabold leading-[1.08] text-slate-900">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl cta-gradient text-white"><Icon className="h-7 w-7" /></span>
            {title}
          </h1>
          <p className="mt-4 text-[16px] text-slate-500">Last updated: {updated}</p>
          {intro && <p className="mt-4 text-[18px] leading-relaxed text-slate-600">{intro}</p>}
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-10">
            {sections.map((s, i) => (
              <div key={i}>
                <h2 className="text-[22px] sm:text-[26px] font-extrabold text-slate-900">{i + 1}. {s.heading}</h2>
                {s.paras?.map((p, j) => (
                  <p key={j} className="mt-3 text-[17px] leading-relaxed text-slate-600">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="mt-3 space-y-2.5">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-3 text-[17px] leading-relaxed text-slate-600">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full cta-gradient" /> {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {footNote && (
            <div className="mt-12 rounded-2xl border border-violet-100 bg-violet-50/50 p-6">
              <p className="text-[16px] leading-relaxed text-slate-700">{footNote}</p>
            </div>
          )}

          <div className="mt-12 rounded-3xl cta-gradient p-8 text-center relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-60" />
            <div className="relative">
              <h3 className="text-[24px] font-extrabold text-white">Still have questions?</h3>
              <p className="mt-2 text-[17px] text-white/85">Reach our team at {company.email}.</p>
              <Link to="/contact" className="mt-6 inline-block">
                <Button className="bg-white text-violet-700 hover:bg-violet-50 rounded-full h-13 h-14 px-7 text-[16px] font-bold">Contact Us <ArrowRight className="ml-2 h-5 w-5" /></Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export function Terms() {
  return (
    <LegalShell
      icon={FileText}
      eyebrow="Legal"
      title="Terms & Conditions"
      seoTitle="Terms & Conditions | MapMyKid"
      seoDescription="Read the terms and conditions for using MapMyKid's cognitive assessment platform, including how assessments and reports may be used, payments, partner terms and governing law."
      updated="July 2025"
      intro="Welcome to MapMyKid. By accessing our website, booking an assessment or using our platform, you agree to these Terms & Conditions. Please read them carefully."
      sections={[
        { heading: "Use of the Service", paras: ["MapMyKid provides cognitive assessments and development resources for children from Nursery to Grade 12. You agree to use the platform only for lawful purposes and to provide accurate information during registration and assessments."] },
        { heading: "Nature of Assessments & Reports", paras: ["Our assessments and cognitive profiles are developmental and educational tools designed to help discover a child's natural abilities, learning speed and mindset. They are not a medical, clinical or psychiatric diagnosis and should not be treated as a substitute for professional advice where such advice is required."] },
        { heading: "Accounts & Responsibility", paras: ["A parent or guardian is responsible for assessments taken by a child. You are responsible for keeping any login details secure and for all activity that takes place under your account."] },
        { heading: "Intellectual Property", paras: ["All content, methodology, reports, worksheets, branding and platform technology are the property of MapMyKid and its licensors. You may not copy, reproduce or resell any part of the service except as expressly permitted under a partner agreement."] },
        { heading: "Payments", paras: ["Fees for assessments and subscriptions are displayed before purchase. All payments are processed through secure third-party payment channels. Prices may be revised from time to time."] },
        { heading: "Partner Terms", paras: ["Partners operating under their own brand using our platform are bound by their separate partner agreement in addition to these terms. Partners are responsible for their own pricing, marketing and customer relationships."] },
        { heading: "Limitation of Liability", paras: ["MapMyKid is provided on an as-is basis. To the maximum extent permitted by law, we are not liable for any indirect or consequential loss arising from the use of the platform or reliance on any report."] },
        { heading: "Changes to These Terms", paras: ["We may update these terms from time to time. Continued use of the platform after changes are posted constitutes acceptance of the revised terms."] },
        { heading: "Governing Law", paras: ["These terms are governed by the laws of India, and any disputes are subject to the exclusive jurisdiction of the courts in India."] },
      ]}
    />
  );
}

export function RefundPolicy() {
  return (
    <LegalShell
      icon={RotateCcw}
      eyebrow="Legal"
      title="Refund Policy"
      seoTitle="Refund Policy | MapMyKid"
      seoDescription="Understand MapMyKid's refund policy for cognitive assessments and partner subscriptions, when refunds apply, how to request one and processing timelines."
      updated="July 2025"
      intro="This Refund Policy explains how refunds are handled for MapMyKid assessments and subscriptions."
      sections={[
        { heading: "Digital Nature of the Service", paras: ["MapMyKid assessments are digital products. Once an assessment link has been generated, activated or attempted, the service is considered delivered and is generally non-refundable."] },
        { heading: "When a Refund May Apply", bullets: [
          "If you were charged more than once for the same assessment due to a technical or payment error.",
          "If payment was successful but the assessment was never activated or made available to you.",
          "If a duplicate purchase was made in error and the assessment has not been attempted.",
        ] },
        { heading: "How to Request a Refund", paras: [`To request a refund, email us at ${company.email} with your payment reference and the reason for the request, within 7 days of the transaction. Our team will review and respond within a reasonable time.`] },
        { heading: "Refund Processing", paras: ["Approved refunds are processed back to the original payment method. Depending on your bank or payment provider, it may take 5 to 10 business days for the amount to reflect."] },
        { heading: "Partner Subscriptions", paras: ["Partner and subscription fees are billed for a fixed term (quarterly or yearly). These fees are non-refundable once the term has begun, except where required by applicable law."] },
      ]}
      footNote="Payment Gateway powered by Addrish Software Solutions Pvt Ltd. All transactions are processed through secure, encrypted channels."
    />
  );
}

export function PrivacyPolicy() {
  return (
    <LegalShell
      icon={Lock}
      eyebrow="Legal"
      title="Privacy Policy"
      seoTitle="Privacy Policy | MapMyKid"
      seoDescription="Learn how MapMyKid collects, uses and protects your and your child's data, including what we collect, cookies, children's data, data sharing, security and your rights."
      updated="July 2025"
      intro="At MapMyKid, we respect your privacy and are committed to protecting the personal information of you and your child. This policy explains what we collect and how we use it."
      sections={[
        { heading: "Information We Collect", bullets: [
          "Contact details such as name, email address and phone number.",
          "Assessment responses and the resulting cognitive profile of the child.",
          "Basic technical data such as browser type and usage analytics.",
        ] },
        { heading: "How We Use Your Information", bullets: [
          "To generate and deliver your child's cognitive assessment report.",
          "To respond to enquiries and provide customer support.",
          "To improve our assessments, content and platform experience.",
          "To send relevant updates, only where you have opted in.",
        ] },
        { heading: "Children's Data", paras: ["Assessments for children are booked and consented to by a parent or guardian. We collect and process a child's data only with such consent and use it solely to provide the assessment and development resources."] },
        { heading: "Cookies", paras: ["We use cookies to understand how visitors interact with our website and to improve performance. You can control cookies through your browser settings."] },
        { heading: "Sharing of Information", paras: ["We do not sell your personal data. Information may be shared with trusted service providers (such as payment processors) strictly to operate the service, or where required by law."] },
        { heading: "Data Security & Retention", paras: ["We use reasonable technical and organisational measures to protect your data, and retain it only for as long as necessary to provide the service and meet legal obligations."] },
        { heading: "Your Rights", paras: [`You may request access to, correction of, or deletion of your personal data by contacting us at ${company.email}.`] },
      ]}
    />
  );
}
